import { useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography
} from "@mui/material";
import { postNotification } from "../../../../components/utilities/Notifications";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import {
  deleteData,
  getData
} from "../../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faGoogle } from "@fortawesome/free-brands-svg-icons";
import { GWWarhammerIcon } from "../../../../components/icons/gw/GWIcons";
import { useEffect } from "react";
import {
  BCPLinkSimple,
  BCPLinkSlash,
  BCPXmark
} from "../../../../components/icons/bcp/BCPIcons";
import { getLanguageCookie } from "../../../../language/LanguageFunctions";
import { useTranslation } from "react-i18next";

export function getAuthFederatedIdentities() {
  const AUTH_FEDERATED_IDENTITIES = getEnvironmentVariable(
    "AUTH_FEDERATED_IDENTITIES"
  );

  let federatedIdentities = [];
  if (
    AUTH_FEDERATED_IDENTITIES !== undefined &&
    AUTH_FEDERATED_IDENTITIES !== "NOT SET"
  ) {
    try {
      federatedIdentities = JSON.parse(AUTH_FEDERATED_IDENTITIES);
    } catch (err) {}
  }
  return federatedIdentities;
}

export function oidButtonSX(identity_provider) {
  return identity_provider === "GamesWorkshop"
    ? {
        backgroundColor: "#fbca1b",
        borderColor: "#181a1c",
        color: "#181a1c",
        "&:hover": {
          backgroundColor: "#E2B618"
        }
      }
    : identity_provider === "Google"
      ? {
          backgroundColor: "#4285F4",
          color: "#FFF",
          "&:hover": {
            backgroundColor: "#3f7ce8"
          }
        }
      : identity_provider === "Facebook"
        ? {
            backgroundColor: "#4267b2",
            color: "#FFF",
            "&:hover": {
              backgroundColor: "#2955ad"
            }
          }
        : {};
}

export function OICLoginButton({
  user,
  setUser,
  processing,
  setProcessing,
  oic_scope,
  identity,
  hide,
  fullWidth,
  hideEndIcon,
  t
}) {
  const { identity_provider } = oic_scope;
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const updateUser = async () => {
    try {
      let apiUser = await getData(`/users/${user.id}`, {
        queryStringParameters: {
          "expand[]": ["team", "bankAccount"]
        }
      });

      let _user = {
        ...user,
        ...apiUser
      };

      setUser(_user);
      setProcessing();
    } catch (err) {
      postNotification("error", err);
    }
  };

  const loginToOIC = async () => {
    let url = `${getEnvironmentVariable(
      "AUTH_HOSTED_UI"
    )}oauth2/idlink?client_id=${getEnvironmentVariable(
      "AUTH_CLIENT_ID"
    )}&redirect_uri=close&identity_provider=${identity_provider}`;
    if (user?.username) {
      url += `&username=${user.username}`;
    }
    const win = window.open(url);
    const timer = setInterval(() => {
      if (win && win?.closed) {
        clearInterval(timer);
        updateUser();
      }
    }, 500);
  };

  const buttonSX = oidButtonSX(identity_provider);

  const buttonComponent = (
    <Button
      variant="contained"
      color="warning"
      sx={{ textTransform: "unset", ...buttonSX }}
      fullWidth={fullWidth}
      onClick={
        identity
          ? null
          : async () => {
              setProcessing(true);
              await loginToOIC();
            }
      }
      startIcon={
        identity_provider === "Google" ? (
          <FontAwesomeIcon icon={faGoogle} />
        ) : identity_provider === "Facebook" ? (
          <FontAwesomeIcon icon={faFacebook} />
        ) : identity_provider === "GamesWorkshop" ? (
          <GWWarhammerIcon />
        ) : null
      }
    >
      {identity
        ? `${
            identity_provider === "GamesWorkshop"
              ? "My Warhammer"
              : identity_provider?.replace(/([A-Z])/g, " $1").trim()
          } Account Linked`
        : `Link with ${
            identity_provider === "GamesWorkshop"
              ? "My Warhammer"
              : identity_provider?.replace(/([A-Z])/g, " $1").trim()
          }`}
    </Button>
  );

  return (
    <>
      {hideEndIcon ? (
        buttonComponent
      ) : (
        <ButtonGroup fullWidth={fullWidth}>
          {buttonComponent}

          <Button
            variant="contained"
            color="warning"
            sx={{ textTransform: "unset", ...buttonSX, maxWidth: "60px" }}
            size="small"
            onClick={async () => {
              if (identity) {
                setUnlinkOpen(true);
              } else {
                setProcessing(true);
                await loginToOIC();
              }
            }}
          >
            {identity ? <BCPLinkSimple /> : <BCPLinkSlash />}
          </Button>
        </ButtonGroup>
      )}
      <UnlinkIdentity
        user={user}
        setUser={setUser}
        processing={processing}
        setProcessing={setProcessing}
        identity={identity}
        open={unlinkOpen}
        setOpen={setUnlinkOpen}
        t={t}
      />
    </>
  );
}

export default function IdentitiesTab(props) {
  const { user, setUser } = props;
  const [loading, setLoading] = useState(true);
  const [identities, setIdentities] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Language setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "pages.account.identities"
  });

  const federatedIdentities = getAuthFederatedIdentities();

  useEffect(() => {
    let _identitiesObject = {};
    if (user.identities) {
      for (let _identity of user.identities) {
        if (_identity.providerName) {
          _identitiesObject[_identity.providerName] = _identity;
        }
      }
    }
    let _identities = federatedIdentities
      ?.filter((s) => s.type !== "cognito")
      .map((s) => {
        s.identity = _identitiesObject[s.amplify?.identity_provider];
        return s;
      });

    setIdentities(_identities);
    setLoading(false);

    // eslint-disable-next-line
  }, [user]);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            {t("connectYourAccounts", {
              defaultValue: "Connect Your Accounts"
            })}
          </Typography>
        </Grid>

        {identities?.length > 0 &&
          identities.map((oic_scope, index) => (
            <Grid key={`oic.${index}`} item xs={12}>
              <OICLoginButton
                fullWidth
                identity={oic_scope.identity}
                oic_scope={oic_scope.amplify}
                processing={processing}
                setProcessing={setProcessing}
                user={user}
                setUser={setUser}
                t={t}
              />
            </Grid>
          ))}
      </Grid>

      {Boolean(processing || loading) && <ModalLoadingFull />}
    </>
  );
}

function UnlinkIdentity({
  user,
  setUser,
  processing,
  setProcessing,
  identity,
  open,
  setOpen,
  t
}) {
  const [password, setPassword] = useState("");
  const [isTouched, setIsTouched] = useState(false);
  const [isPasswordError, setIsPasswordError] = useState(false);

  const onPasswordChange = (e) => {
    const {
      target: { value }
    } = e;

    if (!isTouched) setIsTouched(true);
    setPassword(value);
    setIsPasswordError(!value.length);
  };

  const onClose = () => {
    setPassword("");
    setIsTouched(false);
    setOpen(false);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="delete-identity-account-title"
      aria-describedby="delete-identity-account-description"
      disableAutoFocus
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: ["95vw", "85vw", 600],
          maxHeight: "95vh",
          bgcolor: "background.paper",
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: 1,
          boxShadow: 24,
          p: 4
        }}
      >
        <IconButton
          aria-label="cancel"
          color="error"
          sx={{ position: "absolute", top: "4px", right: "4px" }}
          onClick={onClose}
        >
          <BCPXmark />
        </IconButton>
        <Typography
          id="delete-identity-account-title"
          variant="h6"
          component="h2"
          sx={{ mb: 2 }}
        >
          {t("unlinkAccount.deleteAccount", {
            defaultValue: "Delete connected account"
          })}
        </Typography>
        <Typography
          paragraph
          color="error"
          variant="h6"
          component="h2"
          textTransform="uppercase"
        >
          {t("unlinkAccount.p1", {
            defaultValue: "This process cannot be reversed!"
          })}
        </Typography>
        <Typography id="delete-account-description">
          {t("unlinkAccount.p2", {
            defaultValue:
              "If you wish to continue, please type your password to confirm this decision."
          })}
        </Typography>
        <Grid container spacing={1} sx={{ marginTop: "1rem" }}>
          <Grid item xs={12}>
            <TextField
              error={isTouched && isPasswordError}
              helperText={isTouched && "Required"}
              name="password"
              value={password}
              type="password"
              label={t("password", {
                defaultValue: "Password",
                keyPrefix: "fields"
              })}
              variant="outlined"
              inputProps={{ maxLength: 255 }}
              required
              fullWidth
              onBlur={onPasswordChange}
              onChange={onPasswordChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              onClick={onClose}
              fullWidth
              color="info"
              variant="outlined"
              disabled={processing}
            >
              {t("cancel", { defaultValue: "Cancel", keyPrefix: "buttons" })}
            </Button>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Button
              onClick={async () => {
                setProcessing(true);
                try {
                  let apiUser = await deleteData(
                    `/users/${user.id}/linkedaccount`,
                    {
                      body: {
                        providerName: identity.providerName,
                        password
                      }
                    }
                  );

                  let _user = {
                    ...user,
                    ...apiUser
                  };

                  setUser(_user);
                  window.location.reload();
                } catch (error) {
                  postNotification("error", error);
                } finally {
                  setProcessing(false);
                }
              }}
              fullWidth
              color="error"
              variant="contained"
              disabled={processing || isPasswordError || password === ""}
            >
              {t("delete", { defaultValue: "Delete", keyPrefix: "buttons" })}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
